/**
 * M3X.TRAVEL PR A — Full 5-section Solvait request modal at `/me/travel/new`.
 *
 * Per BUILD_READINESS_M3X_TRAVEL_FULL.md §1.K.2 (Solvait modal screenshots
 * 4-5). Replaces the scaffold that routed back to the hub.
 *
 *   Section 1: Business Travel Type (Domestic / International cards)
 *   Section 2: Purpose + AI Solvait assist
 *   Section 3: Business Travel Details (One Way / Round Trip / Multi-city +
 *              flight leg rows)
 *   Section 4: Build Your Itinerary (Flight + Car + Hotel + Train cards)
 *   Section 5: Supporting Documents (drag-drop)
 *   Footer:    Form completion % + Save As Draft + Submit
 *
 * Form completion % per spec §1.C.3:
 *   travel_type selected           → 20
 *   purpose written                → 20
 *   ≥ 1 segment added              → 40
 *   itinerary description filled   → 10
 *   supporting documents attached  → 10
 *
 * Backend wiring (37 endpoints already shipped in PRs 1-32):
 *   POST /api/travel/requests           — create draft
 *   POST /api/travel/requests/{id}/segments — add segment
 *   POST /api/travel/requests/{id}/submit   — final submit
 */

import {
  Building2,
  Car,
  CloudUpload,
  FileText,
  Globe2,
  Hotel,
  Plane,
  PlusCircle,
  Save,
  Send,
  Sparkles,
  Trash2,
  Train,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PageHeader } from "../../components/ui";
import { useLegalEntityId } from "../../lib/legalEntity";


// Local fetch helper — JSON request with credentials (the canonical
// pattern matches the MyTravelHubPage in pages/travel/index.tsx).
async function _request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const body = await resp.json();
      detail = body.detail || body.message || JSON.stringify(body);
    } catch {
      // Non-JSON body
    }
    throw new Error(detail);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}


type TravelType = "domestic" | "international";
type TripType = "one_way" | "round_trip" | "multi_city";
type SegmentType = "flight" | "car" | "hotel" | "train";

interface SegmentDraft {
  client_id: string; // local-only id before the server returns one
  segment_type: SegmentType;
  trip_type?: TripType | null;
  departure_city?: string;
  arrival_city?: string;
  departure_date?: string;
  return_date?: string;
  departure_time?: string;
  return_time?: string;
  check_in_date?: string;
  check_out_date?: string;
  check_in_time?: string;
  check_out_time?: string;
  location?: string;
  description?: string;
}

interface DocumentDraft {
  name: string;
  size: number;
  type: string;
}


function _newSegment(segment_type: SegmentType): SegmentDraft {
  return {
    client_id: `seg-${Math.random().toString(36).slice(2, 10)}`,
    segment_type,
  };
}


export function RequestTravelModalPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const legalEntityId = useLegalEntityId();

  // ---- Section 1
  const [travelType, setTravelType] = useState<TravelType | null>(null);

  // ---- Section 2
  const [purpose, setPurpose] = useState("");
  const [aiAssistLoading, setAiAssistLoading] = useState(false);

  // ---- Section 3 (Business Travel Details — flight legs)
  const [tripType, setTripType] = useState<TripType>("round_trip");
  const [flightLegs, setFlightLegs] = useState<SegmentDraft[]>([
    _newSegment("flight"),
  ]);

  // ---- Section 4 (Build Your Itinerary — non-flight segments)
  const [extraSegments, setExtraSegments] = useState<SegmentDraft[]>([]);
  const [itineraryDescription, setItineraryDescription] = useState("");

  // ---- Section 5 (Supporting Documents)
  const [documents, setDocuments] = useState<DocumentDraft[]>([]);
  const [docDragOver, setDocDragOver] = useState(false);

  // ---- UI state
  const [employeeId, setEmployeeId] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successRequestNumber, setSuccessRequestNumber] = useState<
    number | null
  >(null);

  // ---- Form completion per spec §1.C.3
  const completionPct = useMemo(() => {
    let pct = 0;
    if (travelType) pct += 20;
    if (purpose.trim()) pct += 20;
    const segmentCount = flightLegs.length + extraSegments.length;
    if (segmentCount > 0) pct += 40;
    if (itineraryDescription.trim()) pct += 10;
    if (documents.length > 0) pct += 10;
    return pct;
  }, [travelType, purpose, flightLegs, extraSegments, itineraryDescription, documents]);

  function _updateLeg(idx: number, patch: Partial<SegmentDraft>) {
    setFlightLegs((legs) =>
      legs.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    );
  }
  function _removeLeg(idx: number) {
    setFlightLegs((legs) => legs.filter((_, i) => i !== idx));
  }

  function _updateSegment(idx: number, patch: Partial<SegmentDraft>) {
    setExtraSegments((segs) =>
      segs.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    );
  }
  function _removeSegment(idx: number) {
    setExtraSegments((segs) => segs.filter((_, i) => i !== idx));
  }

  function _handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDocDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const newDocs = files
      .filter((f) => f.size <= 10 * 1024 * 1024)
      .map((f) => ({ name: f.name, size: f.size, type: f.type }));
    setDocuments((d) => [...d, ...newDocs]);
  }

  function _handleFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    const newDocs = files
      .filter((f) => f.size <= 10 * 1024 * 1024)
      .map((f) => ({ name: f.name, size: f.size, type: f.type }));
    setDocuments((d) => [...d, ...newDocs]);
  }

  async function _aiAssistPurpose() {
    setAiAssistLoading(true);
    try {
      // AI assist is a 1-paragraph polish of the user's draft. We
      // reuse the M21 AI translate/generate primitive for now —
      // future polish: a dedicated travel-purpose-polish prompt.
      const r = (await _request("/api/ai-assist/generate", {
        method: "POST",
        body: JSON.stringify({
          context_kind: "travel_purpose",
          context: { current_text: purpose, travel_type: travelType },
        }),
      })) as { text?: string };
      if (r.text) setPurpose(r.text);
    } catch {
      // Silent fail — AI is optional
    } finally {
      setAiAssistLoading(false);
    }
  }

  async function _persistRequestAndSegments(): Promise<string> {
    // Compute date range from earliest departure / latest return
    const allDates = [...flightLegs, ...extraSegments]
      .flatMap((s) => [
        s.departure_date,
        s.return_date,
        s.check_in_date,
        s.check_out_date,
      ])
      .filter(Boolean) as string[];
    const start_date = allDates.length ? allDates.sort()[0] : undefined;
    const end_date = allDates.length ? allDates.sort().slice(-1)[0] : undefined;

    // 1. Create draft
    const draft = (await _request<{ id: string }>("/api/travel/requests", {
      method: "POST",
      body: JSON.stringify({
        employee_id: employeeId,
        legal_entity_id: legalEntityId,
        travel_type: travelType,
        purpose: [purpose, itineraryDescription]
          .filter((s) => s?.trim())
          .join("\n\n"),
        start_date,
        end_date,
      }),
    })) as { id: string };
    const rid = draft.id;

    // 2. Persist each segment
    for (const leg of flightLegs) {
      await _request(`/api/travel/requests/${rid}/segments`, {
        method: "POST",
        body: JSON.stringify({ ...leg, trip_type: tripType, client_id: undefined }),
      });
    }
    for (const seg of extraSegments) {
      await _request(`/api/travel/requests/${rid}/segments`, {
        method: "POST",
        body: JSON.stringify({ ...seg, client_id: undefined }),
      });
    }
    return rid;
  }

  async function _saveDraft() {
    setSavingDraft(true);
    setErrorMessage(null);
    try {
      if (!employeeId) throw new Error("Employee ID required");
      if (!travelType) throw new Error("Select Domestic or International");
      const rid = await _persistRequestAndSegments();
      navigate(`/operations/transactions/travel`);
      return rid;
    } catch (e: unknown) {
      setErrorMessage(
        e instanceof Error ? e.message : "Failed to save draft",
      );
    } finally {
      setSavingDraft(false);
    }
  }

  async function _submit() {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      if (!employeeId) throw new Error("Employee ID required");
      if (!travelType) throw new Error("Select Domestic or International");
      if (!purpose.trim()) throw new Error("Purpose is required");
      if (flightLegs.length + extraSegments.length === 0)
        throw new Error("At least one segment required");

      const rid = await _persistRequestAndSegments();
      const submitted = (await _request<{ request_number: number }>(
        `/api/travel/requests/${rid}/submit`,
        { method: "POST", body: JSON.stringify({}) },
      )) as { request_number: number };
      setSuccessRequestNumber(submitted.request_number);
    } catch (e: unknown) {
      setErrorMessage(
        e instanceof Error ? e.message : "Failed to submit request",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (successRequestNumber !== null) {
    return (
      <div className="mx-auto max-w-2xl space-y-6 p-6">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6">
          <h2 className="text-xl font-bold text-emerald-900">
            ✓ {t("travel.modal.submitted", "Travel request submitted")}
          </h2>
          <p className="mt-2 text-sm text-emerald-800">
            TLR-{successRequestNumber.toString().padStart(6, "0")}
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => navigate("/me/travel")}
              className="rounded-md bg-brand-deep px-4 py-2 text-sm font-medium text-white"
            >
              {t("travel.modal.back_to_hub", "Back to My Travel")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-32">
      <PageHeader
        title={t("travel.modal.title", "Request a Business Travel")}
        subtitle={t(
          "travel.modal.subtitle",
          "5 sections — type, purpose, itinerary, documents, submit. AI suggests per diem instantly.",
        )}
      />

      {errorMessage && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {errorMessage}
        </div>
      )}

      {/* Employee ID (demo helper — replace with Clerk-resolved when wired) */}
      <div className="rounded-lg border border-border-default bg-surface-default p-4">
        <label className="block text-xs font-medium text-text-tertiary">
          {t("travel.modal.employee_id", "Employee ID")}
        </label>
        <input
          type="text"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
          className="mt-1 w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm"
        />
      </div>

      {/* ============ Section 1 — Business Travel Type ============ */}
      <SectionCard
        index={1}
        title={t("travel.modal.section_1_title", "Business Travel Type")}
        complete={travelType !== null}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TypeCard
            icon={Building2}
            label={t("travel.modal.type_domestic", "Domestic")}
            description={t(
              "travel.modal.type_domestic_desc",
              "Travel within Saudi Arabia",
            )}
            selected={travelType === "domestic"}
            onClick={() => setTravelType("domestic")}
          />
          <TypeCard
            icon={Globe2}
            label={t("travel.modal.type_international", "International")}
            description={t(
              "travel.modal.type_international_desc",
              "Travel outside Saudi Arabia (iqama + visa checks apply)",
            )}
            selected={travelType === "international"}
            onClick={() => setTravelType("international")}
          />
        </div>
      </SectionCard>

      {/* ============ Section 2 — Purpose ============ */}
      <SectionCard
        index={2}
        title={t("travel.modal.section_2_title", "Purpose")}
        complete={!!purpose.trim()}
      >
        <p className="mb-2 text-sm text-text-tertiary">
          {t(
            "travel.modal.purpose_hint",
            "Fill purpose, requirements, and related associations.",
          )}
        </p>
        <textarea
          rows={4}
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          placeholder={t(
            "travel.modal.purpose_placeholder",
            "e.g. Client meeting with Riyadh Industries' key partner in Dubai...",
          )}
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={_aiAssistPurpose}
          disabled={aiAssistLoading || !purpose.trim()}
          className="mt-2 inline-flex items-center gap-2 rounded-md border border-ai-subtle bg-ai-bg px-3 py-1.5 text-xs font-medium text-ai-deep hover:bg-ai-bg/80 disabled:opacity-50"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {aiAssistLoading
            ? t("travel.modal.ai_loading", "Polishing…")
            : t("travel.modal.ai_assist", "AI Solvait — polish my draft")}
        </button>
      </SectionCard>

      {/* ============ Section 3 — Business Travel Details ============ */}
      <SectionCard
        index={3}
        title={t("travel.modal.section_3_title", "Business Travel Details")}
        complete={flightLegs.some((l) => l.departure_city && l.arrival_city)}
      >
        <div className="mb-3 flex gap-2">
          {(["one_way", "round_trip", "multi_city"] as TripType[]).map((tt) => (
            <button
              key={tt}
              type="button"
              onClick={() => setTripType(tt)}
              className={`rounded-full border px-3 py-1 text-xs ${
                tripType === tt
                  ? "border-brand-deep bg-brand-deep text-white"
                  : "border-border-default bg-surface-default text-text-secondary"
              }`}
            >
              {t(
                `travel.modal.trip_${tt}`,
                tt === "one_way"
                  ? "One Way"
                  : tt === "round_trip"
                    ? "Round Trip"
                    : "Multi-city",
              )}
            </button>
          ))}
        </div>

        {flightLegs.map((leg, idx) => (
          <div
            key={leg.client_id}
            className="mb-3 rounded-md border border-border-default bg-surface-subtle p-3"
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-text-tertiary">
                {t("travel.modal.leg", "Flight leg")} {idx + 1}
              </span>
              {flightLegs.length > 1 && (
                <button
                  type="button"
                  onClick={() => _removeLeg(idx)}
                  className="text-rose-600 hover:text-rose-800"
                  aria-label={t("travel.modal.remove_leg", "Remove leg")}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <InputField
                label={t("travel.modal.departure_city", "From")}
                value={leg.departure_city ?? ""}
                onChange={(v) => _updateLeg(idx, { departure_city: v })}
                placeholder="Riyadh"
              />
              <InputField
                label={t("travel.modal.arrival_city", "To")}
                value={leg.arrival_city ?? ""}
                onChange={(v) => _updateLeg(idx, { arrival_city: v })}
                placeholder="Dubai"
              />
              <InputField
                label={t("travel.modal.departure_date", "Departure date")}
                type="date"
                value={leg.departure_date ?? ""}
                onChange={(v) => _updateLeg(idx, { departure_date: v })}
              />
              {tripType !== "one_way" && (
                <InputField
                  label={t("travel.modal.return_date", "Return date")}
                  type="date"
                  value={leg.return_date ?? ""}
                  onChange={(v) => _updateLeg(idx, { return_date: v })}
                />
              )}
              <InputField
                label={t("travel.modal.description", "Description")}
                value={leg.description ?? ""}
                onChange={(v) => _updateLeg(idx, { description: v })}
                placeholder={t(
                  "travel.modal.leg_description_placeholder",
                  "Initial client meeting",
                )}
                widthClass="col-span-1 sm:col-span-2"
              />
            </div>
          </div>
        ))}

        {tripType === "multi_city" && (
          <button
            type="button"
            onClick={() => setFlightLegs((l) => [...l, _newSegment("flight")])}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-default px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-subtle"
          >
            <PlusCircle className="h-3.5 w-3.5" />
            {t("travel.modal.add_leg", "Add another flight leg")}
          </button>
        )}
      </SectionCard>

      {/* ============ Section 4 — Build Your Itinerary ============ */}
      <SectionCard
        index={4}
        title={t("travel.modal.section_4_title", "Build Your Itinerary")}
        complete={extraSegments.length > 0 || flightLegs.length > 0}
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <AddSegmentButton
            icon={Plane}
            label={t("travel.modal.add_flight", "+ Flight")}
            onClick={() =>
              setExtraSegments((s) => [...s, _newSegment("flight")])
            }
          />
          <AddSegmentButton
            icon={Car}
            label={t("travel.modal.add_car", "+ Car")}
            onClick={() => setExtraSegments((s) => [...s, _newSegment("car")])}
          />
          <AddSegmentButton
            icon={Hotel}
            label={t("travel.modal.add_hotel", "+ Hotel")}
            onClick={() =>
              setExtraSegments((s) => [...s, _newSegment("hotel")])
            }
          />
          <AddSegmentButton
            icon={Train}
            label={t("travel.modal.add_train", "+ Train")}
            onClick={() =>
              setExtraSegments((s) => [...s, _newSegment("train")])
            }
          />
        </div>

        {extraSegments.length > 0 && (
          <div className="mt-3 space-y-2">
            {extraSegments.map((seg, idx) => (
              <SegmentRow
                key={seg.client_id}
                segment={seg}
                onChange={(patch) => _updateSegment(idx, patch)}
                onRemove={() => _removeSegment(idx)}
              />
            ))}
          </div>
        )}

        <div className="mt-3">
          <label className="block text-xs font-medium text-text-tertiary">
            {t(
              "travel.modal.itinerary_description",
              "Itinerary notes (optional)",
            )}
          </label>
          <textarea
            rows={2}
            value={itineraryDescription}
            onChange={(e) => setItineraryDescription(e.target.value)}
            placeholder={t(
              "travel.modal.itinerary_description_placeholder",
              "Hotel preference, transportation between meetings, etc.",
            )}
            className="mt-1 w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm"
          />
        </div>
      </SectionCard>

      {/* ============ Section 5 — Supporting Documents ============ */}
      <SectionCard
        index={5}
        title={t("travel.modal.section_5_title", "Supporting Documents")}
        complete={documents.length > 0}
        optional
      >
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDocDragOver(true);
          }}
          onDragLeave={() => setDocDragOver(false)}
          onDrop={_handleDrop}
          className={`flex flex-col items-center justify-center rounded-md border-2 border-dashed p-6 text-center text-sm ${
            docDragOver
              ? "border-brand-deep bg-ai-bg text-ai-deep"
              : "border-border-default bg-surface-subtle text-text-secondary"
          }`}
        >
          <CloudUpload className="mb-2 h-8 w-8" />
          <p>
            {t(
              "travel.modal.drag_drop",
              "Drag PDF / DOC / JPG / PNG here, or click to browse (max 10MB each)",
            )}
          </p>
          <label className="mt-3 cursor-pointer rounded-md border border-border-default bg-surface-default px-3 py-1.5 text-xs hover:bg-surface-subtle">
            {t("travel.modal.browse_files", "Browse files")}
            <input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={_handleFilePick}
              className="hidden"
            />
          </label>
        </div>
        {documents.length > 0 && (
          <ul className="mt-3 space-y-1 text-sm">
            {documents.map((d, i) => (
              <li
                key={`${d.name}-${i}`}
                className="flex items-center justify-between rounded-md border border-border-default bg-surface-default px-3 py-1.5"
              >
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-text-tertiary" />
                  {d.name}{" "}
                  <span className="text-xs text-text-tertiary">
                    ({(d.size / 1024).toFixed(0)} KB)
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setDocuments((docs) => docs.filter((_, j) => j !== i))
                  }
                  className="text-rose-600 hover:text-rose-800"
                  aria-label={t("travel.modal.remove_doc", "Remove document")}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {/* ============ Footer ============ */}
      <div className="fixed inset-x-0 bottom-0 border-t border-border-default bg-surface-default/95 p-4 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4">
          <div className="flex-1">
            <div className="mb-1 flex items-center justify-between text-xs text-text-tertiary">
              <span>
                {t("travel.modal.completion", "Form completion")}
              </span>
              <span>{completionPct}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken">
              <div
                className="h-full bg-brand-deep transition-all"
                style={{ width: `${completionPct}%` }}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={_saveDraft}
            disabled={savingDraft || !employeeId || !travelType}
            className="inline-flex items-center gap-2 rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-secondary hover:bg-surface-subtle disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {savingDraft
              ? t("travel.modal.saving", "Saving…")
              : t("travel.modal.save_draft", "Save As Draft")}
          </button>
          <button
            type="button"
            onClick={_submit}
            disabled={submitting || completionPct < 80}
            className="inline-flex items-center gap-2 rounded-md bg-brand-deep px-4 py-2 text-sm font-medium text-white hover:bg-brand-blue disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {submitting
              ? t("travel.modal.submitting", "Submitting…")
              : t("travel.modal.submit", "Submit")}
          </button>
        </div>
      </div>
    </div>
  );
}


// ===========================================================================
// Sub-components
// ===========================================================================


function SectionCard({
  index,
  title,
  complete,
  optional,
  children,
}: {
  index: number;
  title: string;
  complete: boolean;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border-default bg-surface-default p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-subtle text-xs">
            {index}
          </span>
          {title}
          {optional && (
            <span className="ml-1 text-xs text-text-tertiary">
              (optional)
            </span>
          )}
        </h2>
        {complete && (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
            ✓
          </span>
        )}
      </div>
      {children}
    </section>
  );
}


function TypeCard({
  icon: Icon,
  label,
  description,
  selected,
  onClick,
}: {
  icon: typeof Plane;
  label: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border p-4 text-start transition-all ${
        selected
          ? "border-brand-deep bg-ai-bg ring-2 ring-brand-deep/20"
          : "border-border-default bg-surface-default hover:border-brand-deep/30"
      }`}
    >
      <Icon
        className={`mb-2 h-6 w-6 ${
          selected ? "text-brand-deep" : "text-text-tertiary"
        }`}
      />
      <div className="text-sm font-semibold text-text-primary">{label}</div>
      <div className="mt-1 text-xs text-text-tertiary">{description}</div>
    </button>
  );
}


function AddSegmentButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Plane;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1 rounded-md border border-dashed border-border-default bg-surface-default p-3 text-xs text-text-secondary hover:border-brand-deep/40 hover:bg-surface-subtle"
    >
      <Icon className="h-5 w-5 text-text-tertiary" />
      {label}
    </button>
  );
}


function InputField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  widthClass,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  widthClass?: string;
}) {
  return (
    <div className={widthClass ?? ""}>
      <label className="block text-xs font-medium text-text-tertiary">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded-md border border-border-default bg-surface-default px-2 py-1.5 text-sm"
      />
    </div>
  );
}


function SegmentRow({
  segment,
  onChange,
  onRemove,
}: {
  segment: SegmentDraft;
  onChange: (patch: Partial<SegmentDraft>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const icon = {
    flight: Plane,
    car: Car,
    hotel: Hotel,
    train: Train,
  }[segment.segment_type];
  const Icon = icon;

  return (
    <div className="rounded-md border border-border-default bg-surface-subtle p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs font-medium text-text-tertiary">
          <Icon className="h-3.5 w-3.5" />
          {segment.segment_type}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="text-rose-600 hover:text-rose-800"
          aria-label={t("travel.modal.remove_segment", "Remove segment")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {segment.segment_type === "hotel" ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <InputField
            label={t("travel.modal.hotel_location", "Location")}
            value={segment.location ?? ""}
            onChange={(v) => onChange({ location: v })}
            placeholder="Dubai Marina"
          />
          <InputField
            label={t("travel.modal.hotel_check_in", "Check-in")}
            type="date"
            value={segment.check_in_date ?? ""}
            onChange={(v) => onChange({ check_in_date: v })}
          />
          <InputField
            label={t("travel.modal.hotel_check_out", "Check-out")}
            type="date"
            value={segment.check_out_date ?? ""}
            onChange={(v) => onChange({ check_out_date: v })}
          />
          <InputField
            label={t("travel.modal.description", "Description")}
            value={segment.description ?? ""}
            onChange={(v) => onChange({ description: v })}
            placeholder="5-star preferred near client office"
            widthClass="col-span-1 sm:col-span-2"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <InputField
            label={t("travel.modal.departure_city", "From")}
            value={segment.departure_city ?? ""}
            onChange={(v) => onChange({ departure_city: v })}
          />
          <InputField
            label={t("travel.modal.arrival_city", "To")}
            value={segment.arrival_city ?? ""}
            onChange={(v) => onChange({ arrival_city: v })}
          />
          <InputField
            label={t("travel.modal.departure_date", "Departure date")}
            type="date"
            value={segment.departure_date ?? ""}
            onChange={(v) => onChange({ departure_date: v })}
          />
          <InputField
            label={t("travel.modal.description", "Description")}
            value={segment.description ?? ""}
            onChange={(v) => onChange({ description: v })}
            widthClass="col-span-1 sm:col-span-2"
          />
        </div>
      )}
    </div>
  );
}


export default RequestTravelModalPage;
